import { type CSSProperties, useRef, useState } from "react";
import { Facebook, Mail, MapPin, Phone } from "lucide-react";
import "./landing.css";
import logo from "/asset/MySoft_Quickbill_logo_500x300.png";
import powertreeLogo from "/asset/powertree-logo.png";
import quickbillVideo from "/asset/QuickBill_Video.mp4";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import TermsAndConditionsContent from "./TermsAndConditionsContent";
import PrivacyPolicyContent from "./PrivacyPolicyContent";
import MarketingScrollArea from "./MarketingScrollArea";

/**
 * Landing page
 */

/** Shared design tokens for the public marketing surfaces. */
export const rootVars: CSSProperties = {
  ["--bg" as string]: "#ffffff",
  ["--bg2" as string]: "#f6f8fb",
  ["--ink" as string]: "#0f172a",
  ["--muted" as string]: "#64748b",
  ["--line" as string]: "#e5eaf1",
  ["--card" as string]: "#ffffff",
  ["--chipbg" as string]: "#eef2ff",
  ["--chipink" as string]: "#4f46e5",
  ["--cardshadow" as string]: "0 12px 32px rgba(15,23,42,.09)",
  color: "var(--ink)",
  background: "var(--bg)",
};

const contactDetails = [
  { label: "Call us", value: "07-353 2950", href: "tel:+6073532950", icon: Phone },
  {
    label: "Email us",
    value: "admin@powertree.com.my",
    href: "mailto:admin@powertree.com.my",
    icon: Mail,
  },
  {
    label: "Visit us",
    value: "34-01 Jalan Ponderosa 2/1, Johor Bahru, Malaysia",
    href: "https://www.google.com/maps/search/?api=1&query=34-01+Jalan+Ponderosa+2%2F1%2C+Johor+Bahru%2C+Malaysia",
    icon: MapPin,
    external: true,
  },
  {
    label: "Follow us",
    value: "Mysoft Quickbill",
    href: "https://www.facebook.com/mygst.my/",
    icon: Facebook,
    external: true,
  },
];

// ---- Data for repeated sections ----
const topSolutions = [
  {
    badge: "AI",
    badgeStyle: {
      background: "linear-gradient(135deg,#4172e0,#8b53e6)",
      fontSize: "14px",
    } as CSSProperties,
    title: "PowerTree AI Agent",
    desc: "Gives legacy ERP and accounting systems a new life by integrating AI directly into existing databases and workflows - sales order processing, OCR data extraction, bank reconciliation, financial analysis, business intelligence, report generation, and workflow automation.",
  },
  {
    badge: "CA",
    badgeStyle: { background: "#2563eb", fontSize: "12.5px" } as CSSProperties,
    title: "Cloud Accounting & Payroll",
    desc: "Cloud-based accounting, payroll, HR, employee management, leave, claims, financial reporting, and multi-company management - on secure cloud infrastructure built for SMEs.",
  },
  {
    badge: "MCP",
    badgeStyle: {
      background: "#0b1220",
      fontSize: "12.5px",
      border: "1px solid rgba(255,255,255,.14)",
    } as CSSProperties,
    title: "AI Agent MCP for Microsoft ERP",
    desc: "Automates sales entry, data import, bank reconciliation, and business analysis directly inside your Microsoft ERP - no system replacement required.",
  },
];

const whyItems = [
  {
    title: "Modernize, don't replace",
    desc: "Modernize existing systems without replacing them - protect your investments in ERP and accounting software.",
  },
  {
    title: "E-Invoice compliance expertise",
    desc: "Deep expertise in Malaysia (LHDN MyInvois) and Singapore (IRAS InvoiceNow) e-invoice compliance.",
  },
  {
    title: "Practical AI built for SMEs",
    desc: "Enterprise AI that is affordable and practical, built for real SME workflows.",
  },
  {
    title: "90%+ AI cost reduction",
    desc: "Our optimized AI architecture reduces AI token consumption by more than 90%.",
  },
  {
    title: "Trusted intermediary",
    desc: "Hundreds of businesses using different ERP and accounting systems rely on QuickBill as their e-invoice middleware.",
  },
  {
    title: "Continuous innovation & partnership",
    desc: "Continuous innovation and a long-term technology partner that stays after go-live.",
  },
];

const industries = [
  { code: "RT", color: "#4f46e5", label: "Retail" },
  { code: "FB", color: "#ea580c", label: "Food & Beverage" },
  { code: "WD", color: "#16a34a", label: "Wholesale & Distribution" },
  { code: "MF", color: "#0284c7", label: "Manufacturing" },
  { code: "PS", color: "#9333ea", label: "Professional Services" },
  { code: "HC", color: "#dc2626", label: "Healthcare" },
  { code: "LG", color: "#ca8a04", label: "Logistics" },
  { code: "CN", color: "#475569", label: "Construction" },
  { code: "ME", color: "#4338ca", label: "Multi-entity Enterprises" },
];

/** A footer link either navigates (href) or opens an in-page modal (modal). */
type FooterLink = {
  label: string;
  href?: string;
  modal?: "terms" | "privacy";
};

const footerColumns: { heading: string; links: FooterLink[] }[] = [
  {
    heading: "Product",
    links: [
      { label: "Solutions", href: "#solutions" },
      { label: "Why PowerTree", href: "#why" },
      { label: "Industries", href: "#industries" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About Us", href: "#about" },
      { label: "Contact", href: "#contact" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Terms & Conditions", modal: "terms" },
      { label: "Privacy Policy", modal: "privacy" },
    ],
  },
  {
    heading: "Account",
    links: [
      { label: "Login", href: "/login" },
      { label: "Get Started", href: "/register" },
    ],
  },
];

// ---- Reusable style fragments ----
const cardBase: CSSProperties = {
  background: "var(--card)",
  border: "1px solid var(--line)",
  borderRadius: "16px",
  padding: "32px 30px",
};

const badgeBase: CSSProperties = {
  width: "42px",
  height: "42px",
  borderRadius: "11px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontWeight: 800,
  marginBottom: "18px",
};

const LandingContent = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlayVideo = () => {
    setIsVideoPlaying(true);
    videoRef.current?.play();
  };

  return (
    <div
      className="qb-landing"
      id="top"
      data-screen-label="QuickBill Landing"
      style={rootVars}
    >
      {/* ============ NAV (dark in both themes) ============ */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "18px",
          flexWrap: "wrap",
          padding: "14px clamp(20px,4vw,56px)",
          background: "rgba(11,18,32,.92)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(255,255,255,.08)",
        }}
      >
        <a
          href="#top"
          style={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
          }}
        >
          <img
            src={logo}
            alt="QuickBill"
            style={{ height: "30px", width: "auto", display: "block" }}
          />
        </a>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "clamp(14px,2.5vw,28px)",
            fontSize: "14px",
            fontWeight: 500,
            flexWrap: "wrap",
          }}
        >
          <a
            className="qb-navlink"
            href="#about"
            style={{ color: "#94a3b8", textDecoration: "none" }}
          >
            About
          </a>
          <a
            className="qb-navlink"
            href="#solutions"
            style={{ color: "#94a3b8", textDecoration: "none" }}
          >
            Solutions
          </a>
          <a
            className="qb-navlink"
            href="#why"
            style={{ color: "#94a3b8", textDecoration: "none" }}
          >
            Why PowerTree
          </a>
          <a
            className="qb-navlink"
            href="#industries"
            style={{ color: "#94a3b8", textDecoration: "none" }}
          >
            Industries
          </a>
          <a
            className="qb-navlink"
            href="#contact"
            style={{ color: "#94a3b8", textDecoration: "none" }}
          >
            Contact
          </a>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <a
            className="qb-login-btn"
            href="/login"
            style={{
              color: "#e2e8f0",
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: 600,
              padding: "9px 18px",
              border: "1px solid rgba(255,255,255,.22)",
              borderRadius: "9px",
            }}
          >
            Login
          </a>
          <a
            className="qb-get-started"
            href="/register"
            style={{
              color: "#fff",
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: 600,
              padding: "9px 18px",
              borderRadius: "9px",
              background: "#2563eb",
            }}
          >
            Get Started
          </a>
        </div>
      </div>

      {/* ============ HERO ============ */}
      <div
        style={{
          position: "relative",
          background:
            "linear-gradient(160deg,#0e1c46 0%,#2a3d9e 45%,#7345d6 100%)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "640px",
            height: "640px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle,rgba(139,83,230,.35),transparent 65%)",
            top: "-180px",
            right: "-160px",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: "420px",
            height: "420px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle,rgba(65,114,224,.3),transparent 65%)",
            bottom: "-160px",
            left: "-80px",
          }}
        />
        <div
          style={{
            position: "relative",
            padding:
              "clamp(56px,8vw,104px) clamp(20px,7vw,120px) clamp(56px,7vw,96px)",
            color: "#fff",
            maxWidth: "980px",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "7px 16px",
              borderRadius: "999px",
              background: "rgba(255,255,255,.12)",
              border: "1px solid rgba(255,255,255,.2)",
              fontSize: "13px",
              fontWeight: 600,
              color: "#c7d2fe",
            }}
          >
            The E-Invoice Intermediary Platform by PowerTree Solution Sdn. Bhd.
          </div>
          <h1
            style={{
              margin: "22px 0 0",
              fontSize: "clamp(34px,4.6vw,60px)",
              lineHeight: 1.08,
              fontWeight: 800,
              letterSpacing: "-1.2px",
              textWrap: "balance",
            }}
          >
            Don't Replace Your ERP. Empower It.
          </h1>
          <p
            style={{
              margin: "24px 0 0",
              fontSize: "clamp(15px,1.3vw,17px)",
              lineHeight: 1.7,
              color: "rgba(255,255,255,.8)",
              maxWidth: "660px",
            }}
          >
            QuickBill connects your existing ERP and accounting systems to
            government e-invoicing platforms — LHDN MyInvois in Malaysia and
            IRAS InvoiceNow in Singapore without requiring you to replace the
            software you already own. Comply with regulations, embrace AI, and
            automate operations with minimal disruption.
          </p>
          <div
            style={{
              display: "flex",
              gap: "14px",
              marginTop: "36px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <a
              className="qb-hero-primary"
              href="/register"
              style={{
                textDecoration: "none",
                padding: "14px 30px",
                borderRadius: "10px",
                background: "#fff",
                color: "#1e3a8a",
                fontWeight: 700,
                fontSize: "15px",
              }}
            >
              Get Started
            </a>
            <a
              className="qb-hero-demo"
              href="#video"
              style={{
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "9px",
                padding: "14px 28px",
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,.4)",
                color: "#fff",
                fontWeight: 600,
                fontSize: "15px",
              }}
            >
              <span
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: "9px solid #fff",
                  borderTop: "6px solid transparent",
                  borderBottom: "6px solid transparent",
                  display: "inline-block",
                }}
              />
              Watch Demo
            </a>
            <a
              className="qb-hero-login"
              href="/login"
              style={{
                color: "rgba(255,255,255,.8)",
                fontSize: "14px",
                fontWeight: 600,
                textDecoration: "underline",
                textUnderlineOffset: "3px",
              }}
            >
              Login →
            </a>
          </div>
        </div>
        <div
          style={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
            borderTop: "1px solid rgba(255,255,255,.14)",
          }}
        >
          <div
            style={{
              padding: "24px clamp(20px,3vw,40px) 24px clamp(20px,7vw,120px)",
              borderRight: "1px solid rgba(255,255,255,.14)",
            }}
          >
            <div style={{ fontSize: "13px", fontWeight: 700, color: "#fff" }}>
              E-Invoice · Cloud Accounting · AI Agent
            </div>
            <div
              style={{
                fontSize: "12.5px",
                color: "rgba(255,255,255,.6)",
                marginTop: "3px",
              }}
            >
              Trusted middleware for hundreds of businesses
            </div>
          </div>
          <div
            style={{
              padding: "24px clamp(20px,3vw,40px)",
              borderRight: "1px solid rgba(255,255,255,.14)",
            }}
          >
            <div style={{ fontSize: "13px", fontWeight: 700, color: "#fff" }}>
              🇲🇾 LHDN MyInvois ready
            </div>
            <div
              style={{
                fontSize: "12.5px",
                color: "rgba(255,255,255,.6)",
                marginTop: "3px",
              }}
            >
              Validation, submission, cancellation, monitoring
            </div>
          </div>
          <div style={{ padding: "24px clamp(20px,3vw,40px)" }}>
            <div style={{ fontSize: "13px", fontWeight: 700, color: "#fff" }}>
              🇸🇬 IRAS InvoiceNow (Peppol)
            </div>
            <div
              style={{
                fontSize: "12.5px",
                color: "rgba(255,255,255,.6)",
                marginTop: "3px",
              }}
            >
              Compliant national network exchange
            </div>
          </div>
        </div>
      </div>

      {/* ============ VIDEO ============ */}
      <div
        id="video"
        style={{
          padding: "clamp(48px,7vw,80px) clamp(20px,7vw,120px)",
          background: "var(--bg)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: "36px",
            gap: "32px",
            flexWrap: "wrap",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "clamp(26px,2.8vw,34px)",
              fontWeight: 800,
              letterSpacing: "-.7px",
              maxWidth: "480px",
              lineHeight: 1.15,
            }}
          >
            See how QuickBill works, in two and a half minutes
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "14.5px",
              lineHeight: 1.6,
              color: "var(--muted)",
              maxWidth: "380px",
            }}
          >
            Product capabilities, the e-Invoice workflow, and real business
            automation - shown, not told.
          </p>
        </div>
        <div
          style={{
            position: "relative",
            width: "min(100%, 380px)",
            aspectRatio: "9/16",
            margin: "0 auto",
            borderRadius: "18px",
            overflow: "hidden",
            background: "linear-gradient(160deg,#101a3d,#33418f)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 24px 64px rgba(15,23,42,.25)",
          }}
        >
          <video
            ref={videoRef}
            src={quickbillVideo}
            controls={isVideoPlaying}
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
            }}
            onPause={() => setIsVideoPlaying(false)}
            onEnded={() => setIsVideoPlaying(false)}
          />
          {!isVideoPlaying && (
            <button
              type="button"
              onClick={handlePlayVideo}
              aria-label="Play QuickBill introduction video"
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "16px",
                border: "none",
                background: "rgba(10,16,38,.35)",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <div
                className="qb-play"
                style={{
                  width: "clamp(64px,7vw,88px)",
                  height: "clamp(64px,7vw,88px)",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,.95)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 10px 40px rgba(0,0,0,.35)",
                }}
              >
                <span
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: "24px solid #2a3d9e",
                    borderTop: "15px solid transparent",
                    borderBottom: "15px solid transparent",
                    display: "inline-block",
                    marginLeft: "6px",
                  }}
                />
              </div>
            </button>
          )}
        </div>
      </div>

      {/* ============ ABOUT ============ */}
      <div
        id="about"
        style={{
          padding: "clamp(48px,7vw,80px) clamp(20px,7vw,120px)",
          background: "var(--bg2)",
          borderTop: "1px solid var(--line)",
        }}
      >
        <div
          style={{
            fontSize: "13px",
            fontWeight: 700,
            color: "#6366f1",
            letterSpacing: "1px",
            textTransform: "uppercase",
          }}
        >
          About PowerTree Solution Sdn. Bhd.
        </div>
        <h2
          style={{
            margin: "10px 0 0",
            fontSize: "clamp(26px,2.8vw,34px)",
            fontWeight: 800,
            letterSpacing: "-.7px",
            maxWidth: "720px",
            lineHeight: 1.15,
          }}
        >
          Transforming Traditional Business Software into Intelligent Business
          Solutions
        </h2>
        <p
          style={{
            margin: "20px 0 0",
            fontSize: "15px",
            lineHeight: 1.7,
            color: "var(--muted)",
            maxWidth: "760px",
          }}
        >
          PowerTree Solution Sdn. Bhd. is a technology company specializing in
          E-Invoice Intermediary Solutions, Cloud Business Software, and
          Enterprise AI Agents for Small and Medium Enterprises (SMEs) across
          Malaysia and Singapore. Rather than asking businesses to replace their
          existing systems, we help them modernize what they already have. Our
          flagship platforms are QuickBill and PowerTree AI Agent which help
          businesses transition into the AI era while protecting their existing
          investments in ERP and accounting systems.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
            gap: "20px",
            marginTop: "36px",
          }}
        >
          <div className="qb-card" style={cardBase}>
            <h3
              style={{ margin: "0 0 10px", fontSize: "18px", fontWeight: 700 }}
            >
              Our Vision
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                lineHeight: 1.65,
                color: "var(--muted)",
              }}
            >
              To become Southeast Asia's leading AI-powered business technology
              company, enabling every SME to operate smarter through compliance,
              automation, and Artificial Intelligence.
            </p>
          </div>
          <div className="qb-card" style={cardBase}>
            <h3
              style={{ margin: "0 0 10px", fontSize: "18px", fontWeight: 700 }}
            >
              Our Mission
            </h3>
            <ul
              style={{
                margin: 0,
                paddingLeft: "18px",
                fontSize: "14px",
                lineHeight: 1.75,
                color: "var(--muted)",
              }}
            >
              <li>Simplify E-Invoice compliance across multiple countries.</li>
              <li>
                Help SMEs modernize existing systems instead of replacing them.
              </li>
              <li>Make enterprise AI affordable and practical.</li>
              <li>Deliver continuous innovation.</li>
              <li>Become a long-term technology partner.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* ============ CONTACT ============ */}
      <div
        id="contact"
        style={{
          padding: "clamp(48px,7vw,80px) clamp(20px,7vw,120px)",
          background: "var(--bg)",
          borderTop: "1px solid var(--line)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div
          style={{
            fontSize: "13px",
            fontWeight: 700,
            color: "#6366f1",
            letterSpacing: "1px",
            textTransform: "uppercase",
          }}
        >
          Contact PowerTree Solution
        </div>
        <h2
          style={{
            margin: "10px 0 0",
            fontSize: "clamp(26px,2.8vw,34px)",
            fontWeight: 800,
            letterSpacing: "-.7px",
            lineHeight: 1.15,
          }}
        >
          Talk to our team
        </h2>
        <p
          style={{
            margin: "20px 0 0",
            fontSize: "15px",
            lineHeight: 1.7,
            color: "var(--muted)",
            maxWidth: "760px",
          }}
        >
          Speak with the PowerTree team about QuickBill, ERP integration, cloud
          accounting, and AI solutions.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,400px),1fr))",
            gap: "20px",
            maxWidth: "1200px",
            marginTop: "36px",
          }}
        >
          {contactDetails.map((detail) => {
            const Icon = detail.icon;
            return (
              <a
                key={detail.label}
                className="qb-card"
                href={detail.href}
                target={detail.external ? "_blank" : undefined}
                rel={detail.external ? "noreferrer" : undefined}
                style={{
                  ...cardBase,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "14px",
                  minWidth: 0,
                  padding: "22px",
                  color: "var(--ink)",
                  textDecoration: "none",
                }}
              >
                <span
                  style={{
                    display: "grid",
                    placeItems: "center",
                    flex: "0 0 40px",
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    background: "var(--chipbg)",
                    color: "var(--chipink)",
                  }}
                >
                  <Icon size={19} strokeWidth={2} aria-hidden="true" />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "var(--muted)",
                    }}
                  >
                    {detail.label}
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: 1.5,
                      overflowWrap: "normal",
                    }}
                  >
                    {detail.value}
                  </span>
                </span>
              </a>
            );
          })}
        </div>
      </div>

      {/* ============ SOLUTIONS ============ */}
      <div
        id="solutions"
        style={{
          padding: "clamp(48px,7vw,80px) clamp(20px,7vw,120px)",
          background: "var(--bg2)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: "40px",
            gap: "32px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: "#6366f1",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              The PowerTree solution ecosystem
            </div>
            <h2
              style={{
                margin: "10px 0 0",
                fontSize: "clamp(26px,2.8vw,34px)",
                fontWeight: 800,
                letterSpacing: "-.7px",
              }}
            >
              Core Solutions
            </h2>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "14.5px",
              color: "var(--muted)",
              maxWidth: "420px",
              lineHeight: 1.6,
            }}
          >
            E-Invoice compliance, cloud accounting &amp; payroll, and enterprise
            AI to modernise at your own pace without replacing what already
            works.
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
            gap: "20px",
            marginBottom: "20px",
          }}
        >
          {topSolutions.map((s) => (
            <div key={s.badge} className="qb-card" style={cardBase}>
              <div style={{ ...badgeBase, ...s.badgeStyle }}>{s.badge}</div>
              <h3
                style={{
                  margin: "0 0 10px",
                  fontSize: "18px",
                  fontWeight: 700,
                }}
              >
                {s.title}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  lineHeight: 1.65,
                  color: "var(--muted)",
                }}
              >
                {s.desc}
              </p>
            </div>
          ))}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))",
            gap: "20px",
          }}
        >
          <div
            style={{
              gridColumn: "span 2",
              minWidth: 0,
              background: "#0b1220",
              borderRadius: "16px",
              padding: "32px 34px",
              color: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "12px",
                flexWrap: "wrap",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "19px", fontWeight: 700 }}>
                QuickBill - E-Invoice Intermediary Platform
              </h3>
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: "999px",
                  background: "rgba(129,140,248,.22)",
                  color: "#a5b4fc",
                  fontSize: "11px",
                  fontWeight: 700,
                }}
              >
                FLAGSHIP
              </span>
            </div>
            <p
              style={{
                margin: "0 0 20px",
                fontSize: "13.5px",
                lineHeight: 1.65,
                color: "#94a3b8",
              }}
            >
              Connect legacy ERP and accounting systems to government
              e-invoicing platforms without replacing your existing software.
              Today, QuickBill serves hundreds of businesses as their trusted
              e-invoice middleware, with support for more countries planned as
              e-invoicing becomes mandatory worldwide.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
                gap: "14px",
              }}
            >
              <div
                style={{
                  background: "rgba(255,255,255,.06)",
                  border: "1px solid rgba(255,255,255,.1)",
                  borderRadius: "10px",
                  padding: "14px 16px",
                }}
              >
                <div style={{ fontSize: "13px", fontWeight: 700 }}>
                  🇲🇾 LHDN MyInvois
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#94a3b8",
                    marginTop: "4px",
                  }}
                >
                  Validation · Submission · Cancellation · Status monitoring
                </div>
              </div>
              <div
                style={{
                  background: "rgba(255,255,255,.06)",
                  border: "1px solid rgba(255,255,255,.1)",
                  borderRadius: "10px",
                  padding: "14px 16px",
                }}
              >
                <div style={{ fontSize: "13px", fontWeight: 700 }}>
                  🇸🇬 IRAS InvoiceNow
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#94a3b8",
                    marginTop: "4px",
                  }}
                >
                  Compliant exchange via the Peppol-based national network
                </div>
              </div>
            </div>
          </div>
          <div className="qb-card" style={cardBase}>
            <div
              style={{
                ...badgeBase,
                background: "var(--chipbg)",
                color: "var(--chipink)",
                fontSize: "12.5px",
              }}
            >
              90%
            </div>
            <h3
              style={{ margin: "0 0 10px", fontSize: "18px", fontWeight: 700 }}
            >
              Affordable Enterprise AI
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                lineHeight: 1.65,
                color: "var(--muted)",
              }}
            >
              Our optimized AI architecture reduces AI token consumption by more
              than 90%, making enterprise AI affordable and practical for SMEs.
            </p>
          </div>
        </div>
      </div>

      {/* ============ WHY ============ */}
      <div
        id="why"
        style={{
          padding: "clamp(48px,7vw,80px) clamp(20px,7vw,120px)",
          background: "var(--bg)",
        }}
      >
        <h2
          style={{
            margin: "0 0 40px",
            fontSize: "clamp(26px,2.8vw,34px)",
            fontWeight: 800,
            letterSpacing: "-.7px",
          }}
        >
          Why choose PowerTree
        </h2>
        <div
          className="qb-why-grid"
          style={{
            display: "grid",
            gap: "1px",
            background: "var(--line)",
            border: "1px solid var(--line)",
            borderRadius: "16px",
            overflow: "hidden",
          }}
        >
          {whyItems.map((item) => (
            <div
              key={item.title}
              style={{ background: "var(--card)", padding: "26px 28px" }}
            >
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: 700,
                  marginBottom: "6px",
                }}
              >
                {item.title}
              </div>
              <div
                style={{
                  fontSize: "13.5px",
                  color: "var(--muted)",
                  lineHeight: 1.55,
                }}
              >
                {item.desc}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ============ INDUSTRIES ============ */}
      <div
        id="industries"
        style={{
          padding: "clamp(44px,6vw,72px) clamp(20px,7vw,120px)",
          background: "var(--bg2)",
        }}
      >
        <h2
          style={{
            margin: "0 0 36px",
            fontSize: "clamp(24px,2.5vw,30px)",
            fontWeight: 800,
            letterSpacing: "-.6px",
          }}
        >
          Industries we serve
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))",
            gap: "18px 14px",
          }}
        >
          {industries.map((ind) => (
            <div
              key={ind.code}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  background: "var(--card)",
                  border: "1px solid var(--line)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: "14px",
                  color: ind.color,
                }}
              >
                {ind.code}
              </div>
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "var(--muted)",
                }}
              >
                {ind.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ============ FINAL CTA + FOOTER ============ */}
      <div
        style={{
          background: "#0b1220",
          padding: "clamp(48px,7vw,80px) clamp(20px,7vw,120px) 0",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "560px",
            height: "560px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle,rgba(139,83,230,.28),transparent 65%)",
            top: "-200px",
            right: "-140px",
          }}
        />
        <div
          style={{
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "40px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ maxWidth: "640px" }}>
            <h2
              style={{
                margin: 0,
                fontSize: "clamp(28px,3vw,38px)",
                fontWeight: 800,
                letterSpacing: "-.8px",
              }}
            >
              Make Your Existing Systems Smarter
            </h2>
            <p
              style={{
                margin: "18px 0 0",
                fontSize: "15.5px",
                lineHeight: 1.65,
                color: "#94a3b8",
              }}
            >
              Digital transformation is no longer about replacing systems. It is
              about making existing systems smarter. PowerTree helps businesses
              preserve their investments while embracing compliance, cloud
              technology, and Artificial Intelligence.
            </p>
          </div>
          <a
            className="qb-cta-btn"
            href="/register"
            style={{
              flex: "none",
              textDecoration: "none",
              padding: "16px 34px",
              borderRadius: "10px",
              background: "linear-gradient(135deg,#4172e0,#8b53e6)",
              color: "#fff",
              fontWeight: 700,
              fontSize: "15px",
              boxShadow: "0 12px 32px rgba(91,95,224,.4)",
            }}
          >
            Start Your Digital Transformation
          </a>
        </div>

        {/* footer */}
        <div
          style={{
            position: "relative",
            marginTop: "clamp(48px,6vw,72px)",
            padding: "36px 0 0",
            borderTop: "1px solid rgba(255,255,255,.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "40px",
              flexWrap: "wrap",
              paddingBottom: "32px",
              borderBottom: "1px solid rgba(255,255,255,.08)",
            }}
          >
            <div style={{ maxWidth: "300px" }}>
              <a
                href="#top"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  textDecoration: "none",
                }}
              >
                <img
                  src={logo}
                  alt="QuickBill"
                  style={{ height: "32px", width: "auto", display: "block" }}
                />
              </a>
              <p
                style={{
                  margin: "14px 0 0",
                  fontSize: "13.5px",
                  color: "#64748b",
                }}
              >
                Don't Replace Your ERP. Empower It.
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginTop: "24px",
                  padding: "12px 14px",
                  background: "rgba(255,255,255,.05)",
                  border: "1px solid rgba(255,255,255,.09)",
                  borderRadius: "12px",
                }}
              >
                <img
                  src={powertreeLogo}
                  alt="PowerTree Solution"
                  style={{ width: "34px", height: "auto" }}
                />
                <div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#64748b",
                      letterSpacing: ".4px",
                    }}
                  >
                    POWERED BY
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#e2e8f0",
                    }}
                  >
                    PowerTree Solution Sdn. Bhd.
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "clamp(32px,5vw,72px)",
                fontSize: "13.5px",
                flexWrap: "wrap",
              }}
            >
              {footerColumns.map((col) => (
                <div
                  key={col.heading}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <span
                    style={{
                      color: "#e2e8f0",
                      fontWeight: 700,
                      fontSize: "12px",
                      letterSpacing: ".8px",
                      textTransform: "uppercase",
                    }}
                  >
                    {col.heading}
                  </span>
                  {col.links.map((link) =>
                    link.modal ? (
                      <button
                        key={link.label}
                        type="button"
                        className="qb-footlink"
                        onClick={() =>
                          link.modal === "terms"
                            ? setTermsModalOpen(true)
                            : setPrivacyModalOpen(true)
                        }
                        style={{
                          color: "#94a3b8",
                          background: "none",
                          border: "none",
                          padding: 0,
                          font: "inherit",
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                      >
                        {link.label}
                      </button>
                    ) : (
                      <a
                        key={link.label}
                        className="qb-footlink"
                        href={link.href}
                        style={{ color: "#94a3b8", textDecoration: "none" }}
                      >
                        {link.label}
                      </a>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap",
              padding: "18px 0 28px",
              fontSize: "12.5px",
              color: "#475569",
            }}
          >
            <span>
              © 2026 PowerTree Solution Sdn. Bhd. All Rights Reserved.
            </span>
            <span>
              Developed by PowerTree Solution Sdn. Bhd. · Malaysia & Singapore
            </span>
          </div>
        </div>
      </div>

      <Dialog open={termsModalOpen} onOpenChange={setTermsModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] grid-rows-[auto_minmax(0,1fr)] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Terms and Conditions</DialogTitle>
            <DialogDescription className="sr-only">
              The terms governing your use of the QuickBill platform.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto pr-1">
            <TermsAndConditionsContent />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={privacyModalOpen} onOpenChange={setPrivacyModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] grid-rows-[auto_minmax(0,1fr)] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Privacy Policy</DialogTitle>
            <DialogDescription className="sr-only">
              How QuickBill collects, uses and shares personal data.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto pr-1">
            <PrivacyPolicyContent />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** Landing page inside the shared overlay-scrollbar container. */
const LandingPage = () => (
  <MarketingScrollArea>
    <LandingContent />
  </MarketingScrollArea>
);

export default LandingPage;
